import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/common/DatePicker";
import { toast } from "sonner";
import { CalendarDays, FileSpreadsheet, Printer, Save } from "lucide-react";
import { PageTitle } from "@/components/common/Crud";
import { downloadCSV } from "@/lib/csv";

const MONTH_OPTIONS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function buildMonthDays(year, month) {
  const totalDays = new Date(Number(year), Number(month), 0).getDate();
  return Array.from({ length: totalDays }, (_, index) => {
    const day = index + 1;
    const date = new Date(Number(year), Number(month) - 1, day);
    return {
      day,
      dateKey: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      weekdayIndex: date.getDay(),
      weekdayLabel: WEEKDAY_SHORT[date.getDay()],
      isSunday: date.getDay() === 0,
    };
  });
}

function summarizeAttendance(rows, monthDays) {
  const dateKeys = new Set(monthDays.map((day) => day.dateKey));
  return rows.map((student) => {
    const stats = { P: 0, A: 0, L: 0, HD: 0 };
    Object.entries(student.statusMap).forEach(([dateKey, value]) => {
      if (!dateKeys.has(dateKey)) return;
      if (value === "P" || value === "A" || value === "L" || value === "HD") {
        stats[value] += 1;
      }
    });
    const markedDays = stats.P + stats.A + stats.L + stats.HD;
    return {
      ...student,
      stats,
      percentage: markedDays > 0 ? Math.round((stats.P * 100) / markedDays) : 0,
    };
  });
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function buildRegisterExport(rows, monthDays) {
  const columns = [
    { key: "admission_no", label: "Adm. No." },
    { key: "full_name", label: "Student" },
    ...monthDays.map((day) => ({
      key: day.dateKey,
      label: `${String(day.day).padStart(2, "0")} ${day.weekdayLabel}`,
    })),
    { key: "present", label: "P" },
    { key: "absent", label: "A" },
    { key: "leave", label: "L" },
    { key: "half_day", label: "HD" },
    { key: "percentage", label: "%" },
  ];

  const exportRows = rows.map((student) => {
    const row = {
      admission_no: student.admission_no,
      full_name: student.full_name,
      present: student.stats.P,
      absent: student.stats.A,
      leave: student.stats.L,
      half_day: student.stats.HD,
      percentage: student.percentage,
    };
    monthDays.forEach((day) => {
      row[day.dateKey] = student.statusMap[day.dateKey] || "";
    });
    return row;
  });

  return { columns, rows: exportRows };
}

function buildRegisterPrintHtml({
  rows,
  monthDays,
  className,
  section,
  monthLabel,
  year,
}) {
  const headerCells = monthDays
    .map(
      (day) =>
        `<th class="${day.isSunday ? "sun" : ""}"><div>${String(day.day).padStart(2, "0")}</div><div class="weekday">${escapeHtml(day.weekdayLabel)}</div></th>`,
    )
    .join("");

  const bodyRows = rows
    .map((student) => {
      const dayCells = monthDays
        .map((day) => {
          const value = student.statusMap[day.dateKey] || "-";
          const tone =
            value === "P"
              ? "present"
              : value === "A"
                ? "absent"
                : value === "L"
                  ? "leave"
                  : value === "HD"
                    ? "half"
                    : "empty";
          return `<td class="${day.isSunday ? "sun " : ""}${tone}">${escapeHtml(value)}</td>`;
        })
        .join("");

      return `<tr>
        <td class="student-cell">
          <div class="student-name">${escapeHtml(student.full_name)}</div>
          <div class="student-adm">${escapeHtml(student.admission_no)}</div>
        </td>
        ${dayCells}
        <td class="summary present">${student.stats.P}</td>
        <td class="summary absent">${student.stats.A}</td>
        <td class="summary leave">${student.stats.L}</td>
        <td class="summary half">${student.stats.HD}</td>
        <td class="summary percent">${student.percentage}%</td>
      </tr>`;
    })
    .join("");

  return `<!doctype html>
  <html>
    <head>
      <meta charset="utf-8" />
      <title>Attendance Register ${escapeHtml(monthLabel)} ${escapeHtml(year)}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: Arial, sans-serif; color: #0f172a; margin: 0; }
        .head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 10px; }
        .title { font-size: 20px; font-weight: 700; margin: 0 0 4px; }
        .meta { font-size: 12px; color: #475569; }
        table { width: 100%; border-collapse: collapse; font-size: 10px; }
        th, td { border: 1px solid #cbd5e1; padding: 4px; text-align: center; }
        th { background: #f8fafc; font-weight: 700; }
        .weekday { font-size: 9px; color: #64748b; margin-top: 2px; }
        .student-cell { text-align: left; min-width: 170px; }
        .student-name { font-weight: 700; }
        .student-adm { font-size: 9px; color: #64748b; margin-top: 2px; }
        .sun { background: #fff1f2; }
        .present { color: #15803d; font-weight: 700; }
        .absent { color: #be123c; font-weight: 700; }
        .leave { color: #b45309; font-weight: 700; }
        .half { color: #0369a1; font-weight: 700; }
        .empty { color: #cbd5e1; }
        .summary { font-weight: 700; }
        .percent { background: #ecfdf5; }
      </style>
    </head>
    <body>
      <div class="head">
        <div>
          <div class="title">Monthly Attendance Register</div>
          <div class="meta">Class: ${escapeHtml(className || "-")} | Section: ${escapeHtml(section || "-")} | Month: ${escapeHtml(monthLabel)} ${escapeHtml(year)}</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Student / Date</th>
            ${headerCells}
            <th>P</th>
            <th>A</th>
            <th>L</th>
            <th>HD</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>${bodyRows}</tbody>
      </table>
      <script>window.onload = function(){ window.print(); };</script>
    </body>
  </html>`;
}

export default function Attendance() {
  const { branchId, sessionId } = useAuth();
  const [classes, setClasses] = useState([]);
  const [mode, setMode] = useState("daily");
  const [classId, setClassId] = useState("");
  const [section, setSection] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [students, setStudents] = useState([]);
  const [status, setStatus] = useState({});
  const [reportClassId, setReportClassId] = useState("");
  const [reportSection, setReportSection] = useState("");
  const [reportMonth, setReportMonth] = useState(
    String(new Date().getMonth() + 1).padStart(2, "0"),
  );
  const [reportYear, setReportYear] = useState(
    String(new Date().getFullYear()),
  );
  const [reportRows, setReportRows] = useState([]);
  const [reportBusy, setReportBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const c = await api.get("/classes");
      setClasses(c.data.items || []);
    })();
  }, []);

  const load = useCallback(async () => {
    if (!classId || !section) {
      setStudents([]);
      return;
    }
    const { data } = await api.get("/students", {
      params: {
        branch_id: branchId,
        session_id: sessionId,
        class_id: classId,
        section,
        limit: 500,
      },
    });
    setStudents(data.items || []);
    const { data: ex } = await api.get("/attendance/by-date", {
      params: { date, class_id: classId, section },
    });
    const m = {};
    (data.items || []).forEach((st) => {
      m[st.id] = "P";
    });
    (ex.items || []).forEach((r) => {
      m[r.student_id] = r.status;
    });
    setStatus(m);
  }, [classId, section, date, branchId, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMonthlyRegister = useCallback(async () => {
    if (!reportClassId || !reportSection) {
      setReportRows([]);
      return;
    }

    setReportBusy(true);
    try {
      const monthKey = `${reportYear}-${reportMonth}`;
      const [studentsRes, attendanceRes] = await Promise.all([
        api.get("/students", {
          params: {
            branch_id: branchId,
            session_id: sessionId,
            class_id: reportClassId,
            section: reportSection,
            limit: 1000,
          },
        }),
        api.get("/attendance/monthly", {
          params: {
            branch_id: branchId,
            session_id: sessionId,
            month: monthKey,
          },
        }),
      ]);

      const monthlyStudents = studentsRes.data.items || [];
      const monthlyAttendance = attendanceRes.data.items || [];
      const allowedStudents = new Set(
        monthlyStudents.map((student) => String(student.id)),
      );
      const attendanceMap = new Map();

      monthlyStudents.forEach((student) => {
        attendanceMap.set(String(student.id), {
          id: String(student.id),
          admission_no: student.admission_no || "-",
          full_name:
            `${student.first_name || ""} ${student.last_name || ""}`.trim() ||
            student.admission_no ||
            "Student",
          statusMap: {},
        });
      });

      monthlyAttendance.forEach((entry) => {
        const studentId = String(entry.student_id || "");
        if (
          !allowedStudents.has(studentId) ||
          !attendanceMap.has(studentId) ||
          !entry.date
        ) {
          return;
        }
        attendanceMap.get(studentId).statusMap[entry.date] = entry.status || "";
      });

      setReportRows(Array.from(attendanceMap.values()));
    } catch (error) {
      toast.error(error.message || "Unable to load monthly register");
      setReportRows([]);
    } finally {
      setReportBusy(false);
    }
  }, [
    branchId,
    reportClassId,
    reportMonth,
    reportSection,
    reportYear,
    sessionId,
  ]);

  useEffect(() => {
    if (mode !== "register") {
      return;
    }
    loadMonthlyRegister();
  }, [loadMonthlyRegister, mode]);

  const setAll = (val) => {
    const m = {};
    students.forEach((s) => {
      m[s.id] = val;
    });
    setStatus(m);
  };

  const save = async () => {
    const records = students.map((s) => ({
      student_id: s.id,
      status: status[s.id] || "P",
    }));
    await api.post("/attendance/mark", {
      date,
      class_id: classId,
      section,
      branch_id: branchId,
      session_id: sessionId,
      records,
    });
    toast.success("Attendance saved");
  };

  const cls = classes.find((c) => c.id === classId);
  const sectionsAvailable = cls?.sections || [];
  const reportCls = classes.find((c) => c.id === reportClassId);
  const reportSectionsAvailable = reportCls?.sections || [];
  const monthDays = buildMonthDays(reportYear, reportMonth);
  const summarizedRows = summarizeAttendance(reportRows, monthDays);
  const yearOptions = Array.from({ length: 5 }, (_, index) =>
    String(new Date().getFullYear() - 2 + index),
  );
  const monthLabel =
    MONTH_OPTIONS.find((month) => month.value === reportMonth)?.label ||
    reportMonth;

  const exportMonthlyExcel = () => {
    if (summarizedRows.length === 0) {
      toast.info("No register rows to export");
      return;
    }
    const exportData = buildRegisterExport(summarizedRows, monthDays);
    downloadCSV(
      exportData.rows,
      exportData.columns,
      `attendance-register-${reportCls?.name || "class"}-${reportSection || "section"}-${reportYear}-${reportMonth}.csv`,
    );
  };

  const exportMonthlyPdf = () => {
    if (summarizedRows.length === 0) {
      toast.info("No register rows to export");
      return;
    }
    const popup = window.open("", "_blank", "width=1400,height=900");
    if (!popup) {
      toast.error("Popup blocked. Allow popups to export PDF.");
      return;
    }
    popup.document.write(
      buildRegisterPrintHtml({
        rows: summarizedRows,
        monthDays,
        className: reportCls?.name || "",
        section: reportSection,
        monthLabel,
        year: reportYear,
      }),
    );
    popup.document.close();
    popup.focus();
  };

  return (
    <div data-testid="attendance-page">
      <PageTitle
        title="Student Attendance"
        subtitle={
          mode === "daily"
            ? "Mark daily attendance by class & section"
            : "Monthly register with class, section, month and year filters"
        }
      />

      <div className="mb-3 inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm">
        <button
          type="button"
          onClick={() => setMode("daily")}
          className={`rounded-md px-3 py-2 text-sm font-semibold transition ${
            mode === "daily"
              ? "bg-[var(--erp-primary)] text-white"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          Daily Entry
        </button>
        <button
          type="button"
          onClick={() => setMode("register")}
          className={`rounded-md px-3 py-2 text-sm font-semibold transition ${
            mode === "register"
              ? "bg-[var(--erp-primary)] text-white"
              : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          Monthly Register
        </button>
      </div>

      {mode === "daily" ? (
        <>
          <div className="flex flex-wrap gap-2 mb-3 items-end">
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Date
              </Label>
              <DatePicker
                value={date}
                onChange={setDate}
                className="h-8 w-40 text-xs"
                testId="att-date"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Class
              </Label>
              <select
                value={classId}
                onChange={(e) => {
                  setClassId(e.target.value);
                  setSection("");
                }}
                className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
                data-testid="att-class"
              >
                <option value="">—</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                Section
              </Label>
              <select
                value={section}
                onChange={(e) => setSection(e.target.value)}
                className="h-8 rounded-md border border-slate-300 text-xs px-2 bg-white"
                data-testid="att-section"
                disabled={!classId}
              >
                <option value="">—</option>
                {sectionsAvailable.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="ml-auto flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAll("P")}
                data-testid="all-present"
                className="h-8"
              >
                All Present
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAll("A")}
                data-testid="all-absent"
                className="h-8"
              >
                All Absent
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={students.length === 0}
                className="h-8 erp-primary"
                data-testid="save-attendance"
              >
                <Save className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
            </div>
          </div>

          <div className="border border-slate-200 bg-white rounded-md overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-slate-100 text-xs uppercase text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left border-b border-slate-300 w-12">
                    #
                  </th>
                  <th className="px-3 py-2 text-left border-b border-slate-300">
                    Adm. No.
                  </th>
                  <th className="px-3 py-2 text-left border-b border-slate-300">
                    Student
                  </th>
                  <th className="px-3 py-2 text-left border-b border-slate-300">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center py-8 text-xs text-slate-400"
                    >
                      Select class & section to load students
                    </td>
                  </tr>
                )}
                {students.map((s, i) => (
                  <tr
                    key={s.id}
                    className="border-b border-slate-100 hover:bg-slate-50"
                  >
                    <td className="px-3 py-1.5 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-1.5 font-mono text-xs">
                      {s.admission_no}
                    </td>
                    <td className="px-3 py-1.5">
                      {s.first_name} {s.last_name}
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex gap-1">
                        {["P", "A", "L", "HD"].map((v) => (
                          <button
                            key={v}
                            onClick={() => setStatus({ ...status, [s.id]: v })}
                            data-testid={`att-${s.id}-${v}`}
                            className={`px-2 py-0.5 rounded text-[11px] font-semibold border ${
                              status[s.id] === v
                                ? v === "P"
                                  ? "bg-emerald-600 text-white border-emerald-600"
                                  : v === "A"
                                    ? "bg-red-600 text-white border-red-600"
                                    : v === "L"
                                      ? "bg-amber-600 text-white border-amber-600"
                                      : "bg-blue-600 text-white border-blue-600"
                                : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            {v}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="mb-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-end gap-2 xl:flex-nowrap">
              <div className="min-w-[140px] flex-1 xl:max-w-[160px]">
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Class
                </Label>
                <select
                  value={reportClassId}
                  onChange={(e) => {
                    setReportClassId(e.target.value);
                    setReportSection("");
                  }}
                  className="h-9 w-full rounded-md border border-slate-300 px-2.5 text-sm bg-white"
                  data-testid="monthly-class"
                >
                  <option value="">Select class</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[120px] flex-1 xl:max-w-[140px]">
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Section
                </Label>
                <select
                  value={reportSection}
                  onChange={(e) => setReportSection(e.target.value)}
                  className="h-9 w-full rounded-md border border-slate-300 px-2.5 text-sm bg-white"
                  disabled={!reportClassId}
                  data-testid="monthly-section"
                >
                  <option value="">Select section</option>
                  {reportSectionsAvailable.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[140px] flex-1 xl:max-w-[150px]">
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Month
                </Label>
                <select
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                  className="h-9 w-full rounded-md border border-slate-300 px-2.5 text-sm bg-white"
                  data-testid="monthly-month"
                >
                  {MONTH_OPTIONS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[100px] flex-1 xl:max-w-[110px]">
                <Label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Year
                </Label>
                <select
                  value={reportYear}
                  onChange={(e) => setReportYear(e.target.value)}
                  className="h-9 w-full rounded-md border border-slate-300 px-2.5 text-sm bg-white"
                  data-testid="monthly-year"
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[160px] xl:w-[170px]">
                <Label className="mb-1 block text-xs font-semibold text-transparent">
                  Action
                </Label>
                <Button
                  type="button"
                  className="h-9 w-full erp-primary"
                  onClick={loadMonthlyRegister}
                  disabled={!reportClassId || !reportSection || reportBusy}
                >
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {reportBusy ? "Loading..." : "Load Register"}
                </Button>
              </div>
              <div className="ml-auto flex items-end gap-2 xl:pl-1">
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 min-w-[104px]"
                  onClick={exportMonthlyExcel}
                  disabled={summarizedRows.length === 0}
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 min-w-[96px]"
                  onClick={exportMonthlyPdf}
                  disabled={summarizedRows.length === 0}
                >
                  <Printer className="mr-2 h-4 w-4" /> PDF
                </Button>
              </div>
            </div>
          </div>

          <div className="mb-3 grid grid-cols-2 gap-2 lg:grid-cols-5">
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Students
              </div>
              <div className="mt-1 text-xl font-bold text-slate-900">
                {summarizedRows.length}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Month
              </div>
              <div className="mt-1 text-base font-bold text-slate-900">
                {monthLabel} {reportYear}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Class / Section
              </div>
              <div className="mt-1 text-base font-bold text-slate-900">
                {reportCls?.name || "-"}{" "}
                {reportSection ? `• ${reportSection}` : ""}
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Legend
              </div>
              <div className="mt-1 flex flex-wrap gap-1 text-[11px] font-semibold">
                <span className="rounded bg-emerald-100 px-2 py-1 text-emerald-700">
                  P Present
                </span>
                <span className="rounded bg-rose-100 px-2 py-1 text-rose-700">
                  A Absent
                </span>
                <span className="rounded bg-amber-100 px-2 py-1 text-amber-700">
                  L Leave
                </span>
                <span className="rounded bg-sky-100 px-2 py-1 text-sky-700">
                  HD Half Day
                </span>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Marked Days
              </div>
              <div className="mt-1 text-xl font-bold text-slate-900">
                {summarizedRows.reduce(
                  (sum, row) =>
                    sum +
                    row.stats.P +
                    row.stats.A +
                    row.stats.L +
                    row.stats.HD,
                  0,
                )}
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-700">
                    <th className="sticky left-0 z-20 min-w-[190px] border-b border-slate-200 bg-slate-100 px-3 py-2 text-left text-[11px] font-bold uppercase">
                      Student / Date
                    </th>
                    {monthDays.map((day) => (
                      <th
                        key={day.dateKey}
                        className={`min-w-[46px] border-b border-slate-200 px-1.5 py-1.5 text-center text-[11px] font-bold ${day.isSunday ? "bg-rose-50" : "bg-slate-100"}`}
                      >
                        <div>{String(day.day).padStart(2, "0")}</div>
                        <div className="mt-0.5 text-[10px] font-semibold text-slate-500">
                          {day.weekdayLabel}
                        </div>
                      </th>
                    ))}
                    {[
                      { key: "P", label: "P" },
                      { key: "A", label: "A" },
                      { key: "L", label: "L" },
                      { key: "HD", label: "HD" },
                      { key: "%", label: "%" },
                    ].map((item) => (
                      <th
                        key={item.key}
                        className="min-w-[44px] border-b border-slate-200 bg-slate-100 px-1.5 py-1.5 text-center text-[11px] font-bold uppercase"
                      >
                        {item.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(!reportClassId || !reportSection) && (
                    <tr>
                      <td
                        colSpan={monthDays.length + 6}
                        className="px-4 py-8 text-center text-sm text-slate-400"
                      >
                        Select class and section to view the monthly register.
                      </td>
                    </tr>
                  )}
                  {reportClassId &&
                    reportSection &&
                    !reportBusy &&
                    summarizedRows.length === 0 && (
                      <tr>
                        <td
                          colSpan={monthDays.length + 6}
                          className="px-4 py-8 text-center text-sm text-slate-400"
                        >
                          No students found for the selected filters.
                        </td>
                      </tr>
                    )}
                  {summarizedRows.map((student) => (
                    <tr
                      key={student.id}
                      className="border-b border-slate-100 hover:bg-slate-50/70"
                    >
                      <td className="sticky left-0 z-10 border-r border-slate-100 bg-white px-3 py-2 align-top">
                        <div className="font-semibold text-slate-900">
                          {student.full_name}
                        </div>
                        <div className="mt-1 text-xs font-mono text-slate-500">
                          {student.admission_no}
                        </div>
                      </td>
                      {monthDays.map((day) => {
                        const value = student.statusMap[day.dateKey] || "";
                        const tone =
                          value === "P"
                            ? "text-emerald-700"
                            : value === "A"
                              ? "text-rose-700"
                              : value === "L"
                                ? "text-amber-700"
                                : value === "HD"
                                  ? "text-sky-700"
                                  : "text-slate-300";
                        return (
                          <td
                            key={day.dateKey}
                            className={`px-1.5 py-2 text-center text-xs font-bold ${day.isSunday ? "bg-rose-50/60" : ""} ${tone}`}
                          >
                            {value || "-"}
                          </td>
                        );
                      })}
                      <td className="px-1.5 py-2 text-center text-xs font-semibold text-emerald-700">
                        {student.stats.P}
                      </td>
                      <td className="px-1.5 py-2 text-center text-xs font-semibold text-rose-700">
                        {student.stats.A}
                      </td>
                      <td className="px-1.5 py-2 text-center text-xs font-semibold text-amber-700">
                        {student.stats.L}
                      </td>
                      <td className="px-1.5 py-2 text-center text-xs font-semibold text-sky-700">
                        {student.stats.HD}
                      </td>
                      <td className="px-1.5 py-2 text-center">
                        <span className="inline-flex min-w-[34px] justify-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700">
                          {student.percentage}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
